import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  Select,
  InputLabel,
  Alert,
  Switch,
  FormControlLabel
} from '@mui/material'
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridSortModel
} from '@mui/x-data-grid'
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  TrendingUp as EscalateIcon
} from '@mui/icons-material'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { EscalationService } from '../../../../shared/services/EscalationService'
import { categoryService } from '../../../../shared/services/CategoryService'
import { userService } from '../../../../shared/services/UserService'
import {
  EscalationPath,
  CategorySummary,
  UserSummary,
  CreateEscalationPathRequest,
  UpdateEscalationPathRequest,
  EscalationTriggerType
} from '../../../../shared/types/entities'

interface EscalationPathFormData {
  name: string
  description?: string
  triggerType: EscalationTriggerType
  categoryId?: string
  thresholdMinutes?: number
  escalateToUserId?: string
  escalateToRole?: string
  level: number
  isActive: boolean
}

const escalationPathSchema = yup.object({
  name: yup.string().required('Name is required').min(3, 'At least 3 characters'),
  description: yup.string().optional(),
  triggerType: yup.string().required('Trigger type is required').oneOf(['Manual', 'SLA'] as const),
  categoryId: yup.string().optional(),
  thresholdMinutes: yup.number().optional().when('triggerType', {
    is: 'SLA',
    then: (schema) => schema.required('Threshold is required for SLA escalations').min(1, 'Must be at least 1 minute')
  }),
  escalateToUserId: yup.string().optional(),
  escalateToRole: yup.string().optional(),
  level: yup.number().required('Level is required').min(1, 'Must be at least 1'),
  isActive: yup.boolean().required()
}).required()

function EscalationPaths() {
  const [paths, setPaths] = useState<EscalationPath[]>([])
  const [categories, setCategories] = useState<CategorySummary[]>([])
  const [agents, setAgents] = useState<UserSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'level', sort: 'asc' }
  ])

  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedPath, setSelectedPath] = useState<EscalationPath | null>(null)
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null)

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm<EscalationPathFormData>({
    resolver: yupResolver(escalationPathSchema),
    defaultValues: {
      triggerType: 'SLA',
      level: 1,
      isActive: true,
      categoryId: '',
      escalateToUserId: '',
      escalateToRole: ''
    }
  })

  const triggerType = watch('triggerType')

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    setError(null)
    try {
      const [pathsData, categoriesData, usersData] = await Promise.all([
        EscalationService.getEscalationPaths(),
        categoryService.getCategories(),
        userService.getUsers(1, 500)
      ])
      setPaths(pathsData)
      setCategories(categoriesData)
      setAgents(usersData.data.filter(u => u.role !== 'Customer'))
    } catch (err: any) {
      setError(err.message || 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  const handleCreatePath = async (data: EscalationPathFormData) => {
    try {
      const request: CreateEscalationPathRequest = {
        name: data.name,
        description: data.description || undefined,
        triggerType: data.triggerType,
        categoryId: data.categoryId || undefined,
        thresholdMinutes: data.thresholdMinutes || undefined,
        escalateToUserId: data.escalateToUserId || undefined,
        escalateToRole: data.escalateToRole || undefined,
        level: data.level,
        isActive: data.isActive
      }

      await EscalationService.createEscalationPath(request)
      setSuccessMessage('Escalation path created successfully')
      setCreateDialogOpen(false)
      reset()
      loadData()
    } catch (err: any) {
      setError(err.message || 'Failed to create escalation path')
    }
  }

  const handleUpdatePath = async (data: EscalationPathFormData) => {
    if (!selectedPath) return

    try {
      const request: UpdateEscalationPathRequest = {
        name: data.name,
        description: data.description || undefined,
        triggerType: data.triggerType,
        categoryId: data.categoryId || undefined,
        thresholdMinutes: data.thresholdMinutes || undefined,
        escalateToUserId: data.escalateToUserId || undefined,
        escalateToRole: data.escalateToRole || undefined,
        level: data.level,
        isActive: data.isActive
      }

      await EscalationService.updateEscalationPath(selectedPath.pathId, request)
      setSuccessMessage('Escalation path updated successfully')
      setEditDialogOpen(false)
      setSelectedPath(null)
      reset()
      loadData()
    } catch (err: any) {
      setError(err.message || 'Failed to update escalation path')
    }
  }

  const handleDelete = async () => {
    if (!selectedPath) return

    try {
      await EscalationService.deleteEscalationPath(selectedPath.pathId)
      setSuccessMessage('Escalation path deleted successfully')
      setDeleteDialogOpen(false)
      setSelectedPath(null)
      loadData()
    } catch (err: any) {
      setError(err.message || 'Failed to delete escalation path')
    }
  }

  const openEditDialog = (path: EscalationPath) => {
    setSelectedPath(path)
    reset({
      name: path.name,
      description: path.description || '',
      triggerType: path.triggerType,
      categoryId: path.categoryId || '',
      thresholdMinutes: path.thresholdMinutes ?? undefined,
      escalateToUserId: path.escalateToUserId || '',
      escalateToRole: path.escalateToRole || '',
      level: path.level,
      isActive: path.isActive
    })
    setEditDialogOpen(true)
  }

  const openDeleteDialog = (path: EscalationPath) => {
    setSelectedPath(path)
    setDeleteDialogOpen(true)
  }

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 200
    },
    {
      field: 'triggerType',
      headerName: 'Type',
      width: 100,
      renderCell: (params: GridRenderCellParams<EscalationPath>) => (
        <Chip
          label={params.row.triggerType}
          size="small"
          color={params.row.triggerType === 'SLA' ? 'warning' : 'default'}
        />
      )
    },
    {
      field: 'level',
      headerName: 'Level',
      width: 80
    },
    {
      field: 'categoryName',
      headerName: 'Category',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams<EscalationPath>) => (
        params.row.categoryName || <em style={{ color: '#999' }}>All Categories</em>
      )
    },
    {
      field: 'thresholdMinutes',
      headerName: 'Threshold',
      width: 120,
      renderCell: (params: GridRenderCellParams<EscalationPath>) => (
        params.row.thresholdMinutes ? `${params.row.thresholdMinutes} min` : '-'
      )
    },
    {
      field: 'escalateTo',
      headerName: 'Escalate To',
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams<EscalationPath>) => (
        params.row.escalateToUserName || params.row.escalateToRole || '-'
      )
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 100,
      renderCell: (params: GridRenderCellParams<EscalationPath>) => (
        <Chip
          label={params.row.isActive ? 'Active' : 'Inactive'}
          size="small"
          color={params.row.isActive ? 'success' : 'default'}
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      sortable: false,
      renderCell: (params: GridRenderCellParams<EscalationPath>) => (
        <IconButton
          size="small"
          onClick={(e) => {
            setSelectedPath(params.row)
            setActionMenuAnchor(e.currentTarget)
          }}
        >
          <MoreVertIcon />
        </IconButton>
      )
    }
  ]

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <EscalateIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Escalation Paths
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Configure automatic SLA-based escalation rules
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            reset()
            setCreateDialogOpen(true)
          }}
        >
          Create Path
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={paths}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.pathId}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } }
          }}
        />
      </Box>

      {/* Create/Edit Dialog */}
      <Dialog
        open={createDialogOpen || editDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false)
          setEditDialogOpen(false)
          setSelectedPath(null)
          reset()
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {createDialogOpen ? 'Create Escalation Path' : 'Edit Escalation Path'}
        </DialogTitle>
        <form onSubmit={handleSubmit(createDialogOpen ? handleCreatePath : handleUpdatePath)}>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                label="Name"
                {...register('name')}
                error={!!errors.name}
                helperText={errors.name?.message}
                fullWidth
                required
              />

              <TextField
                label="Description"
                {...register('description')}
                error={!!errors.description}
                helperText={errors.description?.message}
                fullWidth
                multiline
                rows={2}
              />

              <FormControl fullWidth required>
                <InputLabel>Trigger Type</InputLabel>
                <Controller
                  name="triggerType"
                  control={control}
                  render={({ field }) => (
                    <Select {...field} label="Trigger Type">
                      <MenuItem value="SLA">SLA (Time-based)</MenuItem>
                      <MenuItem value="Manual">Manual</MenuItem>
                    </Select>
                  )}
                />
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Controller
                  name="categoryId"
                  control={control}
                  render={({ field }) => (
                    <Select {...field} label="Category">
                      <MenuItem value="">
                        <em>All Categories</em>
                      </MenuItem>
                      {categories.map((cat) => (
                        <MenuItem key={cat.categoryId} value={cat.categoryId}>
                          {cat.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>

              {triggerType === 'SLA' && (
                <TextField
                  label="Threshold (minutes)"
                  type="number"
                  {...register('thresholdMinutes', { valueAsNumber: true })}
                  error={!!errors.thresholdMinutes}
                  helperText={errors.thresholdMinutes?.message || 'Time before escalation triggers'}
                  fullWidth
                  required
                  InputProps={{ inputProps: { min: 1 } }}
                />
              )}

              <TextField
                label="Level"
                type="number"
                {...register('level', { valueAsNumber: true })}
                error={!!errors.level}
                helperText={errors.level?.message || 'Escalation priority level'}
                fullWidth
                required
                InputProps={{ inputProps: { min: 1 } }}
              />

              <FormControl fullWidth>
                <InputLabel>Escalate To User</InputLabel>
                <Controller
                  name="escalateToUserId"
                  control={control}
                  render={({ field }) => (
                    <Select {...field} label="Escalate To User">
                      <MenuItem value="">
                        <em>Use Role Instead</em>
                      </MenuItem>
                      {agents.map((agent) => (
                        <MenuItem key={agent.userId} value={agent.userId}>
                          {agent.fullName} ({agent.role})
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>

              <TextField
                label="Or Escalate To Role"
                {...register('escalateToRole')}
                error={!!errors.escalateToRole}
                helperText="E.g., 'Admin', 'Agent'. Leave empty if user is selected above."
                fullWidth
              />

              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch {...field} checked={field.value} />}
                    label="Active"
                  />
                )}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setCreateDialogOpen(false)
              setEditDialogOpen(false)
              setSelectedPath(null)
              reset()
            }}>
              Cancel
            </Button>
            <Button type="submit" variant="contained">
              {createDialogOpen ? 'Create' : 'Update'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Escalation Path</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the escalation path "{selectedPath?.name}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Actions Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={() => setActionMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            if (selectedPath) openEditDialog(selectedPath)
            setActionMenuAnchor(null)
          }}
        >
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedPath) openDeleteDialog(selectedPath)
            setActionMenuAnchor(null)
          }}
        >
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete
        </MenuItem>
      </Menu>
    </Box>
  )
}

export default EscalationPaths
